import React, { useEffect, useState } from "react";
import { Languages, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * LanguagePicker
 *
 * Dropdown of supported languages with the matching ElevenLabs voice name.
 *
 * Props:
 * - value: ISO code
 * - onChange: (code, { narrative, clinician_note, doctor_narrative }) => void   (cached payloads returned)
 * - source: { type: 'owner' | 'shared', id: string }
 * - initialTranslations: { en: string, es?: string, ... } already present on the report
 * - initialNoteTranslations: { en: string, ... } already present
 * - initialDoctorTranslations: { en: string, ... } already present
 */
export default function LanguagePicker({
  value,
  onChange,
  source,
  initialTranslations = {},
  initialNoteTranslations = {},
  initialDoctorTranslations = {},
}) {
  const [languages, setLanguages] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cache, setCache] = useState({ ...initialTranslations });
  const [noteCache, setNoteCache] = useState({ ...initialNoteTranslations });
  const [doctorCache, setDoctorCache] = useState({ ...initialDoctorTranslations });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/languages");
        setLanguages(data);
      } catch {
        setLanguages({});
      }
    })();
  }, []);

  useEffect(() => { setCache((c) => ({ ...c, ...initialTranslations })); }, [initialTranslations]);
  useEffect(() => { setNoteCache((c) => ({ ...c, ...initialNoteTranslations })); }, [initialNoteTranslations]);
  useEffect(() => { setDoctorCache((c) => ({ ...c, ...initialDoctorTranslations })); }, [initialDoctorTranslations]);

  const handleSelect = async (code) => {
    if (code === value) return;
    if (cache[code]) {
      onChange(code, {
        narrative: cache[code],
        clinician_note: noteCache[code] ?? null,
        doctor_narrative: doctorCache[code] ?? null,
      });
      return;
    }
    if (source.type === "shared") {
      onChange(code, { narrative: cache[code] || cache.en, clinician_note: noteCache[code] ?? null, doctor_narrative: null });
      toast("This translation isn't available on the public link yet.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post(`/reports/${source.id}/translate`, { language: code });
      setCache((c) => ({ ...c, [code]: data.narrative }));
      setNoteCache((c) => ({ ...c, [code]: data.clinician_note }));
      setDoctorCache((c) => ({ ...c, [code]: data.doctor_narrative }));
      onChange(code, {
        narrative: data.narrative,
        clinician_note: data.clinician_note,
        doctor_narrative: data.doctor_narrative,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Translation failed");
    } finally {
      setLoading(false);
    }
  };

  if (!languages) return null;
  const current = languages[value] || languages.en;

  return (
    <div className="no-print inline-flex items-center gap-2 mb-6">
      <Languages className="w-4 h-4" style={{ color: "var(--color-text-secondary)" }} />
      <select
        data-testid="language-picker"
        value={value}
        onChange={(e) => handleSelect(e.target.value)}
        disabled={loading}
        className="border bg-white rounded-md px-3 py-1.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#2A4736] disabled:opacity-60"
        style={{ borderColor: "var(--color-border)" }}
      >
        {Object.entries(languages).map(([code, info]) => (
          <option key={code} value={code}>
            {info.native_name}{cache[code] ? "" : " ✦"}
          </option>
        ))}
      </select>
      {loading && <Loader2 className="w-4 h-4 animate-spin" style={{ color: "var(--color-primary)" }} />}
      {!loading && current && (
        <span className="text-xs" style={{ color: "var(--color-text-secondary)" }}>
          Voice: <span className="font-medium" style={{ color: "var(--color-text-primary)" }}>{current.voice_name}</span>
        </span>
      )}
    </div>
  );
}
